import React from "react";
import { Play, Pause, Rewind, ChevronLeft, ChevronRight, Gauge, Mic, Download } from "lucide-react";

/**
 * TranscriptionLive — professional stage teleprompter.
 *
 * Visual rules:
 *  - 4 lines visible: 1 past (♪, dim) · 1 active (►, bright) · 2 upcoming (○, dimmer)
 *  - Smooth opacity transitions, NO bounce / no flashy karaoke effects
 *  - Maximum contrast for direct sunlight readability
 *
 * Interaction rules:
 *  - The audio is owned by the parent <audio>. We only call onTogglePlay / onSeek.
 *  - PRESS-AND-HOLD anywhere on the lyrics for 2 SECONDS → toggles audio play/pause
 *    (both audio AND teleprompter freeze together, since the prompter follows audio time).
 *  - A normal tap or short touch does nothing (no accidental triggers on stage).
 *  - On mobile: swipe left  → next phrase ; swipe right → previous phrase.
 *  - On desktop: discreet "‹ phrase / phrase ›" buttons in the transport row.
 */
const fmtTime = (t) => {
  if (!Number.isFinite(t) || t < 0) t = 0;
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const LINE_BULLET = {
  past: "♪",
  active: "►",
  next: "○",
};

export const TranscriptionLive = ({
  lines,           // [{time, text}] already speed-scaled
  activeIdx,
  currentTime,
  audioDuration,
  isPlaying,
  audioReady,
  onTogglePlay,
  onSeek,
  // Live correction tools (kept compact so they don't pollute stage view)
  speed,
  onSpeedChange,
  onSpeedCommit,
  onRedoRecord,
  onExportLrc,
}) => {
  const [holdProgress, setHoldProgress] = React.useState(0);
  const holdTimerRef = React.useRef(null);
  const holdRafRef = React.useRef(null);
  const holdStartRef = React.useRef(0);
  const swipeStartRef = React.useRef(null);
  const swipeCancelledRef = React.useRef(false);

  const cancelHold = React.useCallback(() => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    if (holdRafRef.current) {
      cancelAnimationFrame(holdRafRef.current);
      holdRafRef.current = null;
    }
    setHoldProgress(0);
  }, []);

  // Long-press progress + fire toggle at 2s.
  // A swipe (large horizontal movement) cancels the hold so we don't pause by accident.
  const onPointerDown = (e) => {
    // Ignore clicks on inner buttons / inputs (they handle their own logic).
    const tag = (e.target?.tagName || "").toLowerCase();
    if (tag === "button" || tag === "input" || e.target.closest?.("button,input,a,select,textarea")) return;
    swipeCancelledRef.current = false;
    swipeStartRef.current = { x: e.clientX, y: e.clientY, t: performance.now() };
    holdStartRef.current = performance.now();
    const tick = () => {
      const p = Math.min(1, (performance.now() - holdStartRef.current) / 2000);
      setHoldProgress(p);
      if (p < 1) holdRafRef.current = requestAnimationFrame(tick);
    };
    holdRafRef.current = requestAnimationFrame(tick);
    holdTimerRef.current = setTimeout(() => {
      // Pauses or resumes audio (and therefore the teleprompter, which follows audio time).
      onTogglePlay?.();
      if (navigator.vibrate) {
        try { navigator.vibrate(30); } catch (_) { /* noop */ }
      }
      cancelHold();
    }, 2000);
  };

  const onPointerMove = (e) => {
    if (!swipeStartRef.current) return;
    const dx = e.clientX - swipeStartRef.current.x;
    const dy = e.clientY - swipeStartRef.current.y;
    // If the finger drifts > 24px we treat it as a swipe in progress, not a hold.
    if (Math.abs(dx) > 24 || Math.abs(dy) > 24) {
      if (!swipeCancelledRef.current) {
        swipeCancelledRef.current = true;
        cancelHold();
      }
    }
  };

  const onPointerUp = (e) => {
    const start = swipeStartRef.current;
    cancelHold();
    swipeStartRef.current = null;
    if (!start) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    const dt = performance.now() - start.t;
    // Swipe detection: mostly horizontal, > 60px, under 600ms.
    if (Math.abs(dx) > 60 && Math.abs(dy) < 50 && dt < 600) {
      if (dx < 0) jumpToPhrase(+1);
      else jumpToPhrase(-1);
    }
  };

  const jumpToPhrase = React.useCallback((delta) => {
    if (!lines || lines.length === 0) return;
    let target = (activeIdx === -1 && delta > 0) ? 0 : activeIdx + delta;
    target = Math.max(0, Math.min(lines.length - 1, target));
    const t = lines[target]?.time;
    if (typeof t === "number" && Number.isFinite(t)) {
      onSeek?.(t + 0.01);
    }
  }, [lines, activeIdx, onSeek]);

  // Reflow window: previous · current · next · next2
  // When `activeIdx` is -1 (before first line), we still show the first 4 lines as upcoming.
  const view = React.useMemo(() => {
    if (!lines || lines.length === 0) return null;
    if (activeIdx < 0) {
      return {
        past: null,
        current: null,
        next1: lines[0] || null,
        next2: lines[1] || null,
      };
    }
    return {
      past: activeIdx > 0 ? lines[activeIdx - 1] : null,
      current: lines[activeIdx] || null,
      next1: lines[activeIdx + 1] || null,
      next2: lines[activeIdx + 2] || null,
    };
  }, [lines, activeIdx]);

  const remaining = Math.max(0, (audioDuration || 0) - (currentTime || 0));
  const progress = audioDuration > 0 ? Math.min(1, currentTime / audioDuration) : 0;

  const lineRow = (kind, line, sizeClass, opacityClass) => (
    <div
      className={`w-full flex items-baseline gap-3 sm:gap-4 ${sizeClass} ${opacityClass} transition-opacity duration-300`}
      data-testid={`tl-line-${kind}`}
    >
      <span className="text-white/40 shrink-0 select-none" aria-hidden="true">
        {LINE_BULLET[kind]}
      </span>
      <span className="leading-tight break-words">
        {line?.text || ""}
      </span>
    </div>
  );

  return (
    <div className="space-y-3">
      {/* Header: remaining time + thin progress bar */}
      <div className="flex items-center justify-between text-[11px] uppercase tracking-widest text-neutral-400">
        <span className="tabular-nums" data-testid="tl-remaining">
          ⏱  -{fmtTime(remaining)}
        </span>
        <span className="tabular-nums" data-testid="tl-current-time">
          {fmtTime(currentTime)} / {fmtTime(audioDuration)}
        </span>
      </div>
      <div
        className="w-full h-[3px] bg-white/8 rounded-full overflow-hidden"
        data-testid="tl-progress-bar"
        aria-hidden="true"
      >
        <div
          className="h-full bg-white/40"
          style={{ width: `${progress * 100}%`, transition: "width 200ms linear" }}
        />
      </div>

      {/* Teleprompter (4 lines) — long-press anywhere here for pause/resume */}
      <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={cancelHold}
        onContextMenu={(e) => e.preventDefault()}
        className="relative bg-black border border-white/10 rounded-2xl px-5 py-6 sm:px-8 sm:py-10 min-h-[18rem] sm:min-h-[22rem] flex flex-col justify-center gap-3 sm:gap-4 select-none"
        style={{ touchAction: "pan-y", WebkitTapHighlightColor: "transparent" }}
        data-testid="tl-prompter"
        aria-label="Téléprompteur — appui long 2 secondes pour pause/reprise"
        role="region"
      >
        {/* hold progress ring overlay */}
        {holdProgress > 0 && (
          <svg
            className="absolute top-3 right-3 pointer-events-none"
            width="36"
            height="36"
            viewBox="0 0 36 36"
            aria-hidden="true"
          >
            <circle cx="18" cy="18" r="15" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="2" />
            <circle
              cx="18"
              cy="18"
              r="15"
              fill="none"
              stroke="var(--gl-gold, #ffd166)"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeDasharray={2 * Math.PI * 15}
              strokeDashoffset={2 * Math.PI * 15 * (1 - holdProgress)}
              transform="rotate(-90 18 18)"
              style={{ transition: "stroke-dashoffset 40ms linear" }}
            />
          </svg>
        )}

        {view ? (
          <>
            {/* Past line */}
            {lineRow(
              "past",
              view.past,
              "text-lg sm:text-xl",
              view.past ? "opacity-35" : "opacity-0"
            )}
            {/* Active line — large, bright, max contrast */}
            <div
              className="w-full flex items-baseline gap-3 sm:gap-4 text-white text-3xl sm:text-5xl font-display font-bold leading-tight tracking-tight"
              data-testid="tl-line-active"
            >
              <span className="text-[var(--gl-neon)] shrink-0 select-none" aria-hidden="true">
                {LINE_BULLET.active}
              </span>
              <span className="break-words">
                {view.current?.text || (
                  <span className="text-neutral-600 text-xl sm:text-2xl font-normal">
                    {isPlaying ? "En attente du premier vers…" : "Lance la lecture"}
                  </span>
                )}
              </span>
            </div>
            {/* Next lines */}
            {lineRow(
              "next",
              view.next1,
              "text-xl sm:text-2xl",
              view.next1 ? "opacity-65" : "opacity-0"
            )}
            {lineRow(
              "next",
              view.next2,
              "text-lg sm:text-xl",
              view.next2 ? "opacity-40" : "opacity-0"
            )}
          </>
        ) : (
          <div className="text-neutral-600 text-base text-center">
            Aucun timestamp enregistré — passe par l’onglet « Enregistrer ».
          </div>
        )}

        {/* Pause veil — purely visual cue when audio is paused */}
        {!isPlaying && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.25em] uppercase text-neutral-500" aria-hidden="true">
            Pause · Appui long 2 s pour reprendre
          </div>
        )}
      </div>

      {/* Transport: prev phrase · play/pause · next phrase */}
      <div className="grid grid-cols-[auto_auto_1fr_auto_auto] items-center gap-2">
        <button
          onClick={() => onSeek?.(0)}
          className="w-11 h-11 rounded-xl bg-black border border-white/15 flex items-center justify-center hover:border-white/30"
          data-testid="tl-rewind"
          title="Retour au début"
        >
          <Rewind size={16} />
        </button>
        <button
          onClick={() => jumpToPhrase(-1)}
          disabled={activeIdx <= 0}
          className="w-11 h-11 rounded-xl bg-black border border-white/15 flex items-center justify-center hover:border-white/30 disabled:opacity-30"
          data-testid="tl-prev-phrase"
          title="Phrase précédente"
        >
          <ChevronLeft size={18} />
        </button>
        <button
          onClick={() => onTogglePlay?.()}
          disabled={!audioReady}
          className="tap-lg rounded-xl bg-[var(--gl-gold)] text-black font-bold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-40 h-11"
          data-testid="tl-toggle-play"
        >
          {isPlaying ? <Pause size={16} /> : <Play size={16} />}
          {isPlaying ? "Pause" : "Lecture"}
        </button>
        <button
          onClick={() => jumpToPhrase(+1)}
          disabled={!lines || activeIdx >= lines.length - 1}
          className="w-11 h-11 rounded-xl bg-black border border-white/15 flex items-center justify-center hover:border-white/30 disabled:opacity-30"
          data-testid="tl-next-phrase"
          title="Phrase suivante"
        >
          <ChevronRight size={18} />
        </button>
        <span className="text-[10px] text-neutral-500 hidden sm:inline">
          ↔ swipe ou flèches
        </span>
      </div>

      {/* Speed slider — discreet, compact, kept for live tempo correction */}
      <div className="bg-black border border-white/10 rounded-xl px-3 py-2">
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold flex items-center gap-1.5">
            <Gauge size={11} className="text-[var(--gl-gold)]" /> Vitesse défilement
          </div>
          <span
            className="text-[var(--gl-gold)] font-display text-base tabular-nums"
            data-testid="tl-speed-value"
          >
            {Math.round(speed * 100)}%
          </span>
        </div>
        <input
          type="range"
          min="0.5"
          max="1.5"
          step="0.01"
          value={speed}
          onChange={(e) => onSpeedChange(parseFloat(e.target.value))}
          onMouseUp={(e) => onSpeedCommit(parseFloat(e.target.value))}
          onTouchEnd={(e) => onSpeedCommit(parseFloat(e.target.value))}
          className="w-full accent-[var(--gl-gold)] cursor-pointer"
          data-testid="tl-speed-slider"
          aria-label="Vitesse de défilement"
        />
      </div>

      {/* Secondary actions */}
      <div className="flex gap-2">
        <button
          onClick={onRedoRecord}
          className="flex-1 rounded-xl bg-black border border-white/15 text-white py-2.5 text-xs font-bold uppercase tracking-wider hover:border-white/30 flex items-center justify-center gap-1.5"
          data-testid="tl-edit-timestamps"
        >
          <Mic size={13} /> Calibrer les timestamps
        </button>
        <button
          onClick={onExportLrc}
          className="flex-1 rounded-xl bg-black border border-[var(--gl-gold)]/40 text-[var(--gl-gold)] py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[var(--gl-gold)] hover:text-black flex items-center justify-center gap-1.5"
          data-testid="tl-export-lrc"
        >
          <Download size={13} /> Export .LRC
        </button>
      </div>

      {/* Keyboard / gesture cheat sheet */}
      <p className="text-[10px] text-neutral-500 text-center leading-relaxed">
        Appui long 2 s sur les paroles = pause/reprise · ← / → ou swipe = phrase précédente/suivante · Espace = saut +1
      </p>
    </div>
  );
};

export default TranscriptionLive;
